import type { SessionUser } from '@arquivo-morto/contracts';
import {
  Avatar,
  Badge,
  Caption1,
  Caption1Strong,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  Text,
  tokens,
} from '@fluentui/react-components';
import { PersonSwapRegular } from '@fluentui/react-icons';
import { useAuth } from '#src/auth/AuthProvider';
import { useSession } from '#src/auth/session';

const useStyles = makeStyles({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    padding: tokens.spacingHorizontalXS,
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: tokens.borderRadiusCircular,
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover },
    ':focus-visible': {
      outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`,
    },
  },
  popover: { minWidth: '320px', maxWidth: '400px' },
  identity: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalM}`,
  },
  identityText: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  subtle: { color: tokens.colorNeutralForeground3 },
  devNote: {
    display: 'block',
    padding: `0 ${tokens.spacingHorizontalM} ${tokens.spacingVerticalS}`,
    color: tokens.colorNeutralForeground3,
  },
  sectionHeader: {
    display: 'block',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalXS}`,
    color: tokens.colorNeutralForeground3,
  },
});

function RoleBadges({ roles }: Pick<SessionUser, 'roles'>) {
  if (roles.length === 0) {
    return (
      <Badge appearance="tint" size="small" color="subtle">
        Sem perfil
      </Badge>
    );
  }
  return roles.map((role) => (
    <Badge key={role} appearance="tint" size="small" color="brand">
      {role}
    </Badge>
  ));
}

export function UserMenu() {
  const styles = useStyles();
  const { user } = useSession();
  const { source, signOut } = useAuth();
  const { name } = user;
  const avatarColor = source === 'teams' ? 'brand' : 'colorful';

  return (
    <Menu positioning="below-end">
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          className={styles.trigger}
          aria-label={`Conta de ${name}. Abrir menu da conta`}
        >
          <Avatar name={name} color={avatarColor} size={28} />
        </button>
      </MenuTrigger>
      <MenuPopover className={styles.popover}>
        <div className={styles.identity}>
          <Avatar name={name} color={avatarColor} size={40} />
          <div className={styles.identityText}>
            <div className={styles.nameRow}>
              <Text weight="semibold" truncate wrap={false}>
                {name}
              </Text>
              <RoleBadges roles={user.roles} />
            </div>
            <Caption1 className={styles.subtle} truncate wrap={false}>
              {user.email}
            </Caption1>
          </div>
        </div>
        {source === 'dev' && (
          <>
            <MenuDivider />
            <Caption1Strong className={styles.sectionHeader}>
              Modo de desenvolvimento
            </Caption1Strong>
            {signOut ? (
              <MenuList>
                <MenuItem icon={<PersonSwapRegular />} onClick={signOut}>
                  Entrar com outro e-mail
                </MenuItem>
              </MenuList>
            ) : (
              <Caption1 className={styles.devNote}>
                Identidade do Teams, sem SSO.
              </Caption1>
            )}
          </>
        )}
      </MenuPopover>
    </Menu>
  );
}
